/* global affirm */

import { useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { get } from 'lodash';
import { useParams } from 'react-router-dom';
import isClient from 'commons/isClient';
import decodeBase64 from 'commons/decodeBase64';
import { UPDATE_ORDER } from '@/graphql/mutations';
import { setItemInStorage } from 'commons/localStorage';
import { DL_UPCHARGE_PRODUCT_OPTIONS } from 'commons/constants';
import getVariantSkuForSelectedOptions from 'commons/getVariantSkuForSelectedOptions';
import { GET_ORDER_INFO } from '@/graphql/queries';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import usePdpIsEditOrderSuccess from 'components/consumer/PDP/state/pdpIsEditOrderSuccess/usePdpIsEditOrderSuccess';
import useSetPdpIsEditOrderSuccess from 'components/consumer/PDP/state/pdpIsEditOrderSuccess/useSetPdpIsEditOrderSuccess';
import useSetPdpEditOrderErrorMessage from 'components/consumer/PDP/state/pdpEditOrderErrorMessage/useSetPdpEditOrderErrorMessage';
import useResetPdpEditOrderErrorMessage from 'components/consumer/PDP/state/pdpEditOrderErrorMessage/useResetPdpEditOrderErrorMessage';
import usePdpOptionsOrder from 'components/consumer/PDP/state/pdpOptionsOrder/usePdpOptionsOrder';
import usePdpSelectedOptions from 'components/consumer/PDP/state/pdpSelectedOptions/usePdpSelectedOptions';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import { getSimpleSelectedOptionsArray } from '../useCart';
const usePDPOrderEdit = (hookOptions = {}) => {
  const {
    skip
  } = hookOptions;
  const {
    cartid
  } = useParams();
  const cartId = decodeBase64(cartid?.split('?')[0]);
  let orderId = null;
  if (isClient()) {
    const params = new URLSearchParams(window.location.search);
    orderId = decodeBase64(params.get('order_id'));
  }
  const pdpIsEditOrderSuccess = usePdpIsEditOrderSuccess();
  const setPdpIsEditOrderSuccess = useSetPdpIsEditOrderSuccess();
  const pdpSelectedOptions = usePdpSelectedOptions();
  const pdpOptionsOrder = usePdpOptionsOrder();
  const setPdpEditOrderErrorMessage = useSetPdpEditOrderErrorMessage();
  const resetPdpEditOrderErrorMessage = useResetPdpEditOrderErrorMessage();
  const {
    data: optionData
  } = usePDPOptions();
  const {
    data: orderInfo,
    loading,
    error
  } = useSuspenseQuery(GET_ORDER_INFO, {
    fetchPolicy: 'network-only',
    variables: {
      order_id: orderId
    },
    skip: !orderId
  });
  const [updateOrderMutation] = useMutation(UPDATE_ORDER);
  const {
    sku: selectedSKU,
    cartProduct: cartItem,
    productOptions: selectedProductOptions,
    upholsteryLabel
  } = useMemo(() => {
    let sku = '';
    let productOptions = {};
    const order_items = orderInfo?.getOrderInfo?.order?.order_items || [];
    let cartProduct = order_items.length ? order_items.find(c => c.order_cart_id === cartId) : null;
    if (cartProduct) {
      cartProduct = {
        ...cartProduct,
        cart_id: cartProduct.order_cart_id,
        discount_percent: cartProduct.discount ? Math.round(cartProduct.discount / cartProduct.total_cost * 100) : 0
      };
      if (cartProduct.product_options) {
        for (const [option_id, option_value_id] of Object.entries(cartProduct.product_options)) {
          if (option_id && option_value_id) {
            productOptions[parseInt(option_id, 10)] = parseInt(option_value_id, 10);
          }
        }
      }
    }

    // fabric
    if (pdpSelectedOptions[17]) {
      const selection = pdpSelectedOptions[17];
      productOptions[17] = selection;
    }
    // leather
    if (pdpSelectedOptions[18]) {
      const selection_0 = pdpSelectedOptions[18];
      productOptions[18] = selection_0;
    }
    const productOptionsExtended = {};
    const keys = Object.keys(productOptions);
    if (keys.length) {
      for (const key of keys) {
        const optionId = parseInt(key, 10);
        const optionsValueId = productOptions[optionId];
        if (optionData?.options) {
          const option = optionData.options.find(o => o.id === optionId);
          if (option?.values) {
            const optionValue = option.values.find(o_0 => o_0.id === optionsValueId);
            if (optionValue) {
              productOptionsExtended[optionId] = optionValue;
            }
          }
        }
      }
      sku = getVariantSkuForSelectedOptions(productOptionsExtended, pdpOptionsOrder);
    }
    productOptions = getSimpleSelectedOptionsArray(productOptions);
    const productUpholsteryLabel = productOptionsExtended?.[17]?.value || productOptionsExtended?.[18]?.value;
    return {
      sku,
      productOptions,
      upholsteryLabel: productUpholsteryLabel,
      cartProduct
    };
  }, [cartId, optionData?.options, orderInfo?.getOrderInfo?.order?.order_items, pdpOptionsOrder, pdpSelectedOptions]);
  const updateOrder = async ({
    taxTotal,
    grandTotal,
    newCCMethod,
    affirmMethod,
    payPalMethod,
    applePayMethod,
    pointOfSaleMethod,
    existingCCMethod
  }) => {
    resetPdpEditOrderErrorMessage();
    try {
      let payment_token;
      if (affirmMethod) {
        if (orderInfo?.getOrderInfo?.order) {
          const {
            payment
          } = orderInfo.getOrderInfo.order;
          const affirmPayment = payment?.find(p => p.payment_type === 'Affirm');
          payment_token = affirmPayment?.payment_token;
          if (!payment_token) {
            setPdpEditOrderErrorMessage('Payment token not found');
            return;
          }
        }
      }
      if (!Object.keys(selectedProductOptions).length) {
        setPdpEditOrderErrorMessage('Product options not found');
        return;
      }
      setItemInStorage(DL_UPCHARGE_PRODUCT_OPTIONS, selectedProductOptions);
      let res = await updateOrderMutation({
        variables: {
          input: {
            sku: selectedSKU,
            order_id: orderId,
            cart_id: cartItem.cart_id,
            productOptions: selectedProductOptions,
            taxTotal,
            grandTotal,
            newCCMethod,
            affirmMethod,
            payPalMethod,
            applePayMethod,
            pointOfSaleMethod,
            existingCCMethod
          }
        }
      });
      res = get(res, 'data.updateOrder');
      const success = get(res, 'success', false);
      if (success) {
        // affirm method
        if (affirmMethod) {
          if (orderInfo?.getOrderInfo?.order) {
            const {
              shipping,
              billing,
              customer
            } = orderInfo.getOrderInfo.order;
            const {
              location
            } = window;
            const baseUrl = `${location.origin}${location.pathname}`;
            const checkoutInfo = {
              merchant: {
                user_confirmation_url: `${baseUrl}?oid=${orderId}&cart_id=${cartItem.cart_id}&cid=${customer.customer_id}&email=${customer.customer_email}&product=${cartItem.name}&upholstery=${upholsteryLabel}&payment_token=${payment_token}`,
                user_cancel_url: baseUrl,
                user_confirmation_url_action: 'GET',
                name: 'Joybird'
              },
              shipping: {
                name: {
                  first: customer.customer_first_name,
                  last: customer.customer_last_name
                },
                address: {
                  line1: shipping.address_street1,
                  line2: shipping.address_street2 || '',
                  city: shipping.address_city,
                  state: shipping.address_state,
                  country: shipping.address_country,
                  zipcode: shipping.address_zip_code
                },
                phone_number: shipping.address_phone,
                email: customer.customer_email
              },
              billing: {
                name: {
                  first: billing.address_first_name,
                  last: billing.address_last_name
                },
                address: {
                  line1: billing.address_street1,
                  line2: billing.address_street2 || '',
                  city: billing.address_city,
                  state: billing.address_state,
                  country: billing.address_country,
                  zipcode: billing.address_zip_code
                },
                phone_number: billing.address_phone,
                email: customer.customer_email
              },
              items: [{
                sku: selectedSKU,
                display_name: upholsteryLabel,
                unit_price: Math.round((grandTotal - taxTotal) * 100),
                qty: cartItem.quantity
              }],
              metadata: {
                platform_type: '4.0',
                mode: 'modal'
              },
              shipping_amount: 0,
              tax_amount: Math.round(taxTotal * 100),
              total: Math.round(grandTotal * 100)
            };
            affirm.checkout(checkoutInfo);
            affirm.checkout.open();
          }
        } else {
          setPdpIsEditOrderSuccess(true);
        }
      } else {
        const message = get(res, 'errors.0.message', null);
        if (message) setPdpEditOrderErrorMessage(message);
      }
    } catch (e) {
      setPdpEditOrderErrorMessage(e.message);
    }
  };
  if (skip) {
    return {
      data: {}
    };
  }
  return {
    data: {
      orderId,
      cartItem,
      selectedSKU,
      selectedMaterialLabel: upholsteryLabel,
      selectedProductOptions,
      isSuccess: pdpIsEditOrderSuccess,
      orderInfo: orderInfo?.getOrderInfo?.order
    },
    actions: {
      updateOrder
    },
    error,
    loading
  };
};
export default usePDPOrderEdit;